import { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import SponsorsContactBg from '../../images/sponsors/bg/sponsor.jpg';
import { addSponsor } from '../../services/sponsors';

function SponsorsContact() {
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    comments: '',
  });
  const popupRef = useRef(null);

  useEffect(() => {
    if (showPopup && popupRef.current) {
      gsap.fromTo(popupRef.current, 
        { scale: 0.95, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.3, ease: 'power3.out' }
      );
    }
  }, [showPopup]);

  const togglePopup = () => setShowPopup(!showPopup);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addSponsor(formData);
    setShowPopup(false); 
  };

  return (
    <div className="relative flex flex-col lg:flex-row w-full lg:min-h-screen">
        <div className="relative w-full lg:w-1/2 min-h-[50vh] lg:min-h-screen bg-slate-950 flex items-center justify-center p-6 sm:p-8 md:p-16 lg:p-20">

        <div className="relative z-10 max-w-lg w-full">
          <p className="text-mainBlue font-bold text-xs uppercase tracking-widest mb-3 md:mb-4 font-custom">Become a Partner</p>
          <h1 className="font-custom text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight">
            Be part of the <span className="text-mainBlue">change</span>
          </h1>
          <p className="text-gray-300 text-sm sm:text-base md:text-lg mb-3 md:mb-4 font-custom leading-relaxed">
            Partnering with us means gaining visibility in major events and academic platforms, while contributing to innovation, sustainability, and the education of future engineers.
          </p>
          <p className="text-gray-400 text-xs sm:text-sm md:text-base mb-6 md:mb-10 font-custom">
            Want to help? Find out how you can contribute to our journey.
          </p>

          <button
            onClick={togglePopup}
            className="group w-full sm:w-fit px-6 sm:px-8 py-3.5 sm:py-4 bg-white text-slate-900 font-bold rounded-full hover:bg-mainBlue hover:text-white transition-all duration-300 hover:scale-105 font-custom flex items-center justify-center gap-3 shadow-xl text-sm sm:text-base"
          >
            Contact us
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </button>
        </div>
      </div>

      <div 
        className="relative w-full lg:w-1/2 h-64 lg:min-h-screen"
        style={{
          backgroundImage: `url(${SponsorsContactBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/50 via-transparent to-transparent lg:bg-gradient-to-l" />
      </div>

      {showPopup && (
        <div 
          className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4"
          onClick={togglePopup}
        >
          <div 
            ref={popupRef}
            className="relative w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={togglePopup}
              className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center rounded-full bg-mainBlue text-white shadow-lg hover:scale-110 transition-transform z-10"
            >
              &times;
            </button>
            
            <div className="bg-white rounded-2xl p-5 sm:p-6 md:p-8 shadow-2xl max-h-[85vh] overflow-y-auto">
              <h2 className="font-custom text-xl sm:text-2xl font-bold text-gray-900 mb-1">Get in Touch</h2>
              <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6 font-custom">Fill out the form and we'll get back to you.</p>
              
              <form className="flex flex-col gap-3 sm:gap-4" onSubmit={handleSubmit}>
                <div>
                  <label className="text-gray-600 text-xs font-semibold uppercase tracking-wider mb-1 block font-custom">Name</label>
                  <input
                    type="text"
                    name="name"
                    className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 w-full text-sm sm:text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-mainBlue focus:ring-2 focus:ring-mainBlue/10 transition-all font-custom"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label className="text-gray-600 text-xs font-semibold uppercase tracking-wider mb-1 block font-custom">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 w-full text-sm sm:text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-mainBlue focus:ring-2 focus:ring-mainBlue/10 transition-all font-custom"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label className="text-gray-600 text-xs font-semibold uppercase tracking-wider mb-1 block font-custom">Company</label>
                  <input
                    type="text"
                    name="company"
                    className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 w-full text-sm sm:text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-mainBlue focus:ring-2 focus:ring-mainBlue/10 transition-all font-custom"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label className="text-gray-600 text-xs font-semibold uppercase tracking-wider mb-1 block font-custom">Phone <span className="text-gray-400 font-normal normal-case">(optional)</span></label>
                  <input
                    type="text"
                    name="phone"
                    className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 w-full text-sm sm:text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-mainBlue focus:ring-2 focus:ring-mainBlue/10 transition-all font-custom"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label className="text-gray-600 text-xs font-semibold uppercase tracking-wider mb-1 block font-custom">Message</label>
                  <textarea
                    name="comments"
                    className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 w-full text-sm sm:text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-mainBlue focus:ring-2 focus:ring-mainBlue/10 transition-all font-custom resize-none"
                    rows="3"
                    value={formData.comments}
                    onChange={handleChange}
                  />
                </div>

                  <button
                    type="submit"
                    className="mt-1 sm:mt-2 w-full py-2.5 sm:py-3 bg-mainBlue text-white font-bold rounded-full hover:bg-blue-600 transition-all duration-300 hover:scale-105 font-custom text-sm sm:text-base shadow-lg"
                  >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SponsorsContact;